import { useForm } from '@tanstack/react-form';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button, Label, Textarea } from '@virtual-care/shadcn';
import {
  FormMessage,
  Loading,
  QuotaBanner,
  useAction,
} from '@virtual-care/ui';
import { AttachFilesField, uploadFile } from '@virtual-care/media-web';

import * as inputs from '../../components/inputs.ts';
import { Header, ProfileCard } from '../../components/header/index.ts';
import { useUser } from '../../firebase.tsx';

const printingFormSchema = z.object({
  description: z
    .string()
    .min(1, { message: 'printing.validation.descriptionRequired' }),
  files: inputs.files.refine((files) => (files?.length ?? 0) > 0, {
    message: 'printing.validation.filesRequired',
  }),
});

export default function Printing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: identity, isLoading: isLoadingIdentity } = useUser();

  const createRequest = useAction('POST /printing-requests/b2b', {
    invalidate: ['GET /patients/me/printing-requests', 'GET /patients/me'],
  });

  const form = useForm({
    defaultValues: {
      description: '',
      files: [] as File[],
    } as z.infer<typeof printingFormSchema>,
    validators: {
      onSubmit: printingFormSchema,
      onSubmitAsync: async ({ value }) => {
        const fileIds = await Promise.all(
          (value.files ?? []).map((file) => uploadFile(file)),
        );

        const result = await createRequest.mutateAsync({
          description: value.description,
          files: fileIds.length > 0 ? fileIds : undefined,
        });

        toast.success(
          t('printing.submittedWithRemaining', {
            remaining: result.remainingPrintingUsageLimit,
          }),
        );
        navigate(`/printing/${result.id}`);
        return undefined;
      },
    },
  });

  if (isLoadingIdentity) return <Loading />;

  const printingQuota = identity?.b2bProfile?.printingUsageLimit ?? 0;

  return (
    <>
      <Header>
        <ProfileCard />
      </Header>
      <div className="min-h-screen px-4">
        <div className="mx-auto max-w-3xl">
          <button
            onClick={() => navigate('/')}
            className="text-muted-foreground mb-4 flex items-center gap-1 text-sm hover:underline"
          >
            <ChevronLeft className="h-4 w-4 ltr:rotate-180" />
            <span>{t('common.back')}</span>
          </button>

          <h1 className="mb-2 text-2xl font-semibold text-gray-900">
            {t('printing.title')}
          </h1>
          <p className="text-muted-foreground mb-6 text-sm">
            {t('printing.subtitle')}
          </p>

          <QuotaBanner
            className="mb-8"
            remaining={printingQuota}
            label={t('printing.quotaRemaining')}
            hint={t('printing.topUpHint')}
          />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="space-y-6"
          >
            <form.Field name="description">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>
                    {t('printing.form.description')}
                  </Label>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    rows={5}
                    placeholder={t('printing.form.descriptionPlaceholder')}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="files">
              {(field) => (
                <div className="space-y-2">
                  <Label>{t('printing.form.files')}</Label>
                  <AttachFilesField field={field} />
                </div>
              )}
            </form.Field>

            <FormMessage errors={form.state.errors} />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={createRequest.isPending || printingQuota <= 0}
              >
                {createRequest.isPending
                  ? t('printing.form.submitting')
                  : t('printing.form.submit')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

